import { Divider, Paper, Theme } from '@mui/material';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import makeStyles from '@mui/styles/makeStyles';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useMutation } from 'react-query';
import { useDispatch, useSelector } from 'react-redux';
import { Prompt, useHistory } from 'react-router';
import { useRecoilStateLoadable } from 'recoil';
import { IUserAtom, user } from '../../recoil/atoms/user';
import { getApiMutation } from '../../services/rest/getApiMutation';
import {
  endpointEnum,
  makeApiRequestCached,
} from '../../services/rest/makeAPIRequest';
import { useSnackbarWithStyles } from '../components/notifications/SnackbarProviderWithStyles';
import {
  setHasClosedCompleteOnboardingModal,
  setTouring,
  updateDisplayOnboarding,
  updateOnboardingStatus,
  updateOnboardingSubmit,
} from '../store/onboardingSlice';
import AccountSettings from './onboardingWalkthrough/AccountSettings';
import { AddAgents } from './onboardingWalkthrough/AddAgents';
import BuildTeams from './onboardingWalkthrough/BuildTeams';
import { CompleteOnboardingModal } from './onboardingWalkthrough/CompleteOnboardingModal';
import Confirmation from './onboardingWalkthrough/Confirmation';
import TeamSettings from './onboardingWalkthrough/TeamSettings';

const useStyles = makeStyles((theme: Theme) => ({
  tabs: {
    borderBottom:
      theme.palette.mode === 'dark' ? '1px solid #515151' : '1px solid #e0e0e0',
    indicator: {
      backgroundColor: '#0098C7',
    },
  },
  tab: {
    textTransform: 'none',
    minWidth: 72,
    fontWeight: theme.typography.fontWeightRegular,
    marginRight: theme.spacing(4),
    fontFamily: [
      '-apple-system',
      'BlinkMacSystemFont',
      '"Segoe UI"',
      'Roboto',
      '"Helvetica Neue"',
      'Arial',
      'sans-serif',
      '"Apple Color Emoji"',
      '"Segoe UI Emoji"',
      '"Segoe UI Symbol"',
    ].join(','),
    '&:hover': {
      color: '#40a9ff',
      opacity: 1,
    },
    '&[aria-selected=true]': {
      color: '#0098C7',
      fontWeight: theme.typography.fontWeightMedium,
    },
  },
  paper: {
    flexGrow: 1,
  },
  root: {
    width: '100%',
  },
  button: {
    marginRight: theme.spacing(1),
  },
  instructions: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
  },
  onboardingSubtitle: {
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.54)',
    marginBottom: '1rem',
    textTransform: 'uppercase',
  },
  confirmationSubtitle: {
    fontSize: '1rem',
    color: 'rgba(0, 0, 0, 0.54)',
  },
  confirmationRow: {
    marginBottom: '16px',
    display: 'flex',
    flexDirection: 'row',
    widht: '100%',
    flexWrap: 'wrap',
  },
}));

export interface IOnboarding {
  businessId: number;
  status: 'processing' | 'complete';
  queues: number;
  totalQueues: number;
  agents: number;
  totalAgents: number;
  cadences: number;
  totalCadences: number;
  powerlists: number;
  totalPowerlists: number;
  teams: number;
  totalTeams: number;
  ivrs: number;
  totalIVRs: number;
  errors: string[];
  createdDate: string;
  updatedDate: string;
}

const CONFIRMATION_STEP = 4;

export function useInterval(callback, delay) {
  if (typeof callback !== 'function') {
    throw new Error('callback must be a function');
  }

  if (typeof delay !== 'number') {
    throw new Error('delay must be a number');
  }

  const savedCallback = useRef<any>();

  useEffect(() => {
    savedCallback.current = callback;
  }, [callback]);

  useEffect(() => {
    function tick() {
      savedCallback.current();
    }

    const id = setInterval(tick, delay);

    return () => {
      clearInterval(id);
    };
  }, [callback, delay]);
}

export default function OnboardingWalkthroughTabs() {
  const history = useHistory();

  // Redirect if onboarding is disabled
  useEffect(() => {
    if (process.env.VITE_APP_ONBOARDING !== '1') {
      history.replace('/');
    }
  }, [history]);

  // If onboarding is disabled, don't render anything while redirecting
  if (process.env.VITE_APP_ONBOARDING !== '1') {
    return null;
  }

  const classes = useStyles();
  const [userAtomLoadable, setUserAtom] = useRecoilStateLoadable(user);
  const [activeStep, setActiveStep] = useState(0);

  const userAtom = userAtomLoadable.contents as IUserAtom;

  const mutation = useMutation(getApiMutation(userAtom, setUserAtom));
  const { enqueueSnackbarWithStyles } = useSnackbarWithStyles();
  const [owWizardDataParsed, setOwWizardDataParsed] = useState();
  const onboardingSelector = useSelector((state) => state?.onboarding);
  const {
    hasClosedCompleteOnboardingModal,
    hasSubmittedOnboarding,
    status,
    touring,
  } = onboardingSelector;
  const [
    _cachedOW,
    onboardingWalkthroughLoadable,
    refetchOnboardingWalkthrough,
  ] = makeApiRequestCached(
    {
      method: 'POST',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
      endpoint: endpointEnum.GET_OW,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );
  const dispatch = useDispatch();
  const hideSuccess =
    (onboardingSelector?.hasClosedCompleteOnboardingModal &&
      status === 'complete') ||
    onboardingSelector?.touring;

  useEffect(() => {
    refetchOnboardingWalkthrough({});
  }, []);

  const onboardingWalkthrough = useMemo(
    () => onboardingWalkthroughLoadable?.contents?.response?.data?.data,
    [onboardingWalkthroughLoadable]
  );

  useEffect(() => {
    onboardingWalkthroughLoadable?.state === 'hasValue' &&
      dispatch(
        updateDisplayOnboarding(onboardingWalkthrough?.displayOnboarding)
      );
  }, [onboardingWalkthrough]);

  useEffect(() => {
    if (onboardingWalkthrough?.owWizardData) {
      const ow = JSON.parse(onboardingWalkthrough?.owWizardData);
      setOwWizardDataParsed(ow);
      if (ow && ow?.activeStep !== activeStep) {
        setActiveStep(ow?.activeStep);
      }
    }
  }, [onboardingWalkthrough]);

  const [
    _cachedOnboarding,
    onboardingLoadable,
    refetchOnboarding,
  ] = makeApiRequestCached<IOnboarding>(
    {
      endpoint: endpointEnum.GET_OW_STATUS,
      method: 'put',
      stage: 'itn',
      domain: process.env.VITE_APP_CDK_DOMAIN,
    },
    (loadable, cached) =>
      loadable.contents?.response?.data?.data ?? cached.current
  );

  const [isOnboardingLoading, setIsOnboardingLoading] = useState<
    undefined | boolean
  >(undefined);

  useEffect(() => {
    setIsOnboardingLoading(onboardingLoadable.state === 'loading');
  }, [onboardingLoadable]);

  const [onboarding, setOnboarding] = useState<any>();

  useEffect(() => {
    setOnboarding(onboardingLoadable?.contents?.response?.data?.data);
  }, [onboardingLoadable]);

  const [cachedOnboarding, setCachedOnboarding] = useState<IOnboarding>();

  useEffect(() => {
    if (onboardingLoadable.state === 'hasValue') {
      setCachedOnboarding(onboardingLoadable?.contents?.response?.data?.data);
    }
  }, [onboardingLoadable]);

  useEffect(() => {
    // if onboarding has been reset
    if (
      onboardingWalkthrough?.displayOnboarding === true &&
      (onboarding?.status === '' || !onboarding?.status)
    ) {
      hasClosedCompleteOnboardingModal &&
        dispatch(setHasClosedCompleteOnboardingModal(false));
      hasSubmittedOnboarding && dispatch(updateOnboardingSubmit(false));
      touring && dispatch(setTouring(false));
    }
  }, [onboardingWalkthrough, onboarding]);

  const [
    isProcessingCompleteOnboarding,
    setIsProcessingCompleteOnboarding,
  ] = useState(false);

  const [
    isCompleteOnboardingModalVisible,
    setIsCompleteOnboardingModalVisible,
  ] = useState(false);

  const [
    completeOnboardingRejectedReason,
    setCompleteOnboardingRejectedReason,
  ] = useState();

  useEffect(() => {
    const isProcessing = onboarding?.status === 'processing';

    if (isOnboardingLoading === false) {
      setIsProcessingCompleteOnboarding(isProcessing);
    }

    const isComplete = onboarding?.status === 'complete';
    dispatch(updateOnboardingStatus(onboarding?.status));
    if (hideSuccess) {
      setIsCompleteOnboardingModalVisible(false);
    } else {
      if (isComplete && !hasClosedCompleteOnboardingModal) {
        setIsCompleteOnboardingModalVisible(true);
      } else if (isComplete && hasClosedCompleteOnboardingModal) {
        setHasClosedCompleteOnboardingModal(false);
      }
    }
  }, [onboarding, isOnboardingLoading, hideSuccess]);

  useInterval(
    async () => {
      refetchOnboarding({});
    },
    isProcessingCompleteOnboarding ? 2000 : 20000
  );

  const [submittedCount, setSubmittedCount] = useState(0);
  const submitForm = () => {
    setSubmittedCount(submittedCount + 1);
  };

  function getStepContent(step: number) {
    switch (step) {
      case 0:
        return (
          <AccountSettings
            loading={onboardingWalkthroughLoadable?.state === 'loading'}
            userAtom={userAtom}
            onboardingClasses={classes}
            mutation={mutation}
            enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
            handleStep={(step) => {
              refetchOnboardingWalkthrough({});
              setActiveStep(step);
            }}
            owWizardDataParsed={owWizardDataParsed}
          />
        );
      case 1:
        return (
          <BuildTeams
            loading={onboardingWalkthroughLoadable?.state === 'loading'}
            onboardingClasses={classes}
            handleStep={(step) => {
              refetchOnboardingWalkthrough({});
              setActiveStep(step);
            }}
            handleBack={(step) => setActiveStep(step)}
            mutation={mutation}
            userAtom={userAtom}
            enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
            activeStep={activeStep}
            owWizardDataParsed={owWizardDataParsed}
          />
        );
      case 2:
        return (
          <TeamSettings
            loading={onboardingWalkthroughLoadable?.state === 'loading'}
            onboardingClasses={classes}
            handleStep={(step) => {
              refetchOnboardingWalkthrough({});
              setActiveStep(step);
            }}
            handleBack={(step) => setActiveStep(step)}
            mutation={mutation}
            userAtom={userAtom}
            enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
            activeStep={activeStep}
            owWizardDataParsed={owWizardDataParsed}
          />
        );
      case 3:
        return (
          <AddAgents
            loading={onboardingWalkthroughLoadable?.state === 'loading'}
            onboardingClasses={classes}
            handleStep={(step) => {
              refetchOnboardingWalkthrough({});
              setActiveStep(step);
            }}
            handleBack={(step) => {
              setActiveStep(step);
            }}
            mutation={mutation}
            userAtom={userAtom}
            activeStep={activeStep}
            owWizardDataParsed={owWizardDataParsed}
          />
        );
      case CONFIRMATION_STEP:
        return (
          <Confirmation
            loading={onboardingWalkthroughLoadable?.state === 'loading'}
            onboardingClasses={classes}
            owWizardDataParsed={owWizardDataParsed}
            userAtom={userAtom}
            handleStep={(step) => setActiveStep(step)}
            activeStep={activeStep}
            mutation={mutation}
            enqueueSnackbarWithStyles={enqueueSnackbarWithStyles}
            isProcessingCompleteOnboarding={isProcessingCompleteOnboarding}
            setIsProcessingCompleteOnboarding={
              setIsProcessingCompleteOnboarding
            }
            setIsCompleteOnboardingModalVisible={
              setIsCompleteOnboardingModalVisible
            }
            setHasClosedCompleteOnboardingModal={
              setHasClosedCompleteOnboardingModal
            }
            setCompleteOnboardingRejectedReason={
              setCompleteOnboardingRejectedReason
            }
            refetchOnboarding={refetchOnboarding}
            submittedCount={submittedCount}
            setOnboarding={setOnboarding}
            setCachedOnboarding={setCachedOnboarding}
            setIsOnboardingLoading={setIsOnboardingLoading}
          />
        );
      default:
        return 'Unknown step';
    }
  }

  const steps = [
    {
      label: 'Account Settings',
      description: 'Account Settings',
    },
    {
      label: 'Build Teams',
      description: 'Build Teams',
    },
    {
      label: 'Team Settings',
      description: 'Team Settings',
    },
    {
      label: 'Add Agents',
      description: 'Add Agents',
    },
    {
      label: 'Confirmation',
      description: 'Confirmation',
    },
  ];

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  return (
    <>
      <CompleteOnboardingModal
        open={isCompleteOnboardingModalVisible}
        onClose={() => {
          setIsCompleteOnboardingModalVisible(false);
          onboardingSelector?.status === 'complete' &&
            dispatch(setHasClosedCompleteOnboardingModal(true));
        }}
        isOnboardingLoading={isOnboardingLoading}
        onboarding={onboarding}
        isProcessingCompleteOnboarding={isProcessingCompleteOnboarding}
        completeOnboardingRejectedReason={completeOnboardingRejectedReason}
        owWizardDataParsed={owWizardDataParsed}
        submitForm={submitForm}
        cachedOnboarding={cachedOnboarding}
      />
      <Paper className={classes.paper}>
        {activeStep != CONFIRMATION_STEP ? (
          <Prompt
            when={onboardingWalkthrough?.displayOnboarding}
            message="You have unsaved onboarding changes, are you sure you want to leave? To save your onboarding progress click continue."
          />
        ) : null}

        <div className={classes.root}>
          <Stepper activeStep={activeStep}>
            {steps.map((step, index) => {
              const stepProps: { completed?: boolean } = {};
              return (
                <Step key={step.label} {...stepProps}>
                  <StepButton
                    disabled={isProcessingCompleteOnboarding || hideSuccess}
                    onClick={handleStep(index)}
                  >
                    {step.label}
                  </StepButton>
                </Step>
              );
            })}
          </Stepper>
          <Divider />
          <div>
            <div>
              <div className={classes.instructions}>
                {getStepContent(activeStep)}
              </div>
            </div>
          </div>
        </div>
      </Paper>
    </>
  );
}
